import { createContext, useContext, useEffect, useReducer } from 'react';
import { toast } from 'react-toastify';
import { AuthService, TokenManager } from '../services';
import { getApiErrorMessage } from '../services/api';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  LOAD_USER_START: 'LOAD_USER_START',
  LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
  LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.LOAD_USER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOAD_USER_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.LOAD_USER_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    case AUTH_ACTIONS.UPDATE_PROFILE:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext(null);

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user on app start
  useEffect(() => {
    loadUser();
  }, []);

  // Load user from token
  const loadUser = async () => {
    dispatch({ type: AUTH_ACTIONS.LOAD_USER_START });

    try {
      const token = TokenManager.getToken();
      const savedUser = TokenManager.getUser();

      if (token && savedUser) {
        // Try to refresh user data from server
        try {
          const response = await AuthService.getProfile();
          if (response.success) {
            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
              payload: response.data.user,
            });
            TokenManager.setUser(response.data.user);
            return;
          }
        } catch (error) {
          const status = error?.response?.status;

          if (status === 401 || status === 403) {
            TokenManager.removeToken();
            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_FAILURE,
              payload: 'Session expired. Please sign in again.',
            });
            return;
          }

          // If non-auth server request fails, use saved user data
          console.warn('Failed to refresh user data:', error);
        }

        // Fallback to saved user data
        dispatch({
          type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
          payload: savedUser,
        });
      } else {
        dispatch({
          type: AUTH_ACTIONS.LOAD_USER_FAILURE,
          payload: 'No authentication token found',
        });
      }
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOAD_USER_FAILURE,
        payload: getApiErrorMessage(error, 'Failed to load user'),
      });
      TokenManager.removeToken();
    }
  };

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await AuthService.login(credentials);
      
      if (response.success) {
        const { user, token } = response.data;
        
        // Verify user has agent role
        if (user.role !== 'agent' && user.role !== 'admin') {
          throw new Error('Access denied. Agent permissions required.');
        }

        // Store token and user data
        TokenManager.setToken(token);
        TokenManager.setUser(user);

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token },
        });

        toast.success(`Welcome back, ${user.name}!`);
        return { success: true, user };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Login failed');
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      // Continue with logout even if server request fails
      console.warn('Logout request failed:', error);
    } finally {
      TokenManager.removeToken();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logged out successfully');
    }
  };

  // Update profile function
  const updateProfile = async (data) => {
    try {
      const response = await AuthService.updateProfile(data);
      
      if (response.success) {
        const updatedUser = response.data.user;
        TokenManager.setUser(updatedUser);
        
        dispatch({
          type: AUTH_ACTIONS.UPDATE_PROFILE,
          payload: updatedUser,
        });

        toast.success('Profile updated successfully');
        return { success: true, user: updatedUser };
      } else {
        throw new Error(response.message || 'Profile update failed');
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Profile update failed');
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Change password function
    // Change password
  const changePassword = async (passwordData) => {
    try {
      const response = await AuthService.changePassword(passwordData);
      
      if (response.success) {
        toast.success('Password changed successfully');
        return response;
      } else {
        throw new Error(response.message || 'Failed to change password');
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to change password');
      toast.error(errorMessage);
      throw error;
    }
  };

  // Register new agent
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      const response = await AuthService.register(userData);
      
      if (response.success && response.data) {
        const { user, token } = response.data;
        
        // Store token
        TokenManager.setToken(token);
        
        // Update state
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token }
        });
        
        toast.success('Account created successfully! Welcome to UsedAdda.');
        return response;
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Registration failed');
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    loadUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;